import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ChevronRight, Upload, FileText } from 'lucide-react';

// ✅ Config Imports
import { IS_DEV, TEST_REAL_API } from "@/config/env";

// ✅ Validation Imports
import { identityProofSchema } from './doctorValidation';
import type { IdentityProofData } from './doctorValidation';

// ✅ API Import
import { saveDoctorIdentity } from '../../../../../api/onboardingApis/doctorOnboardingApi/doctorIdentity.api';

interface DoctorStep3Props {
  onNext: (data: IdentityProofData) => void;
}

export default function DoctorStep3({ onNext }: DoctorStep3Props) {
  const [fileName, setFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    clearErrors,
    trigger, // 🔥 IMPORTED TRIGGER
  } = useForm<IdentityProofData>({
    resolver: zodResolver(identityProofSchema),
    mode: "all", // Changed to 'all' for better responsiveness
    shouldUnregister: true, 
  });

  // 🧹 Effect: Register the file field manually (Fixes the conflict)
  useEffect(() => {
    register("identityProof");
  }, [register]);

  // ⚡ DEV SKIP HANDLER
  const handleDevSkip = async () => {
  
    await new Promise(resolve => setTimeout(resolve, 800));

    // Mock Data
    const mockData: IdentityProofData = {
      identityProof: new DataTransfer().files 
    };

    toast.success("⚡ Mock Identity Saved (Dev Mode)");
    onNext(mockData);
  };

  // 🟢 SUBMIT HANDLER
  const onSubmit = async (data: IdentityProofData) => {
    setIsSubmitting(true);
    try {
       if (IS_DEV && !TEST_REAL_API) {
          await handleDevSkip();
          setIsSubmitting(false);
          return; 
       }

   

      const expertCode = localStorage.getItem('expertCode');
      if (!expertCode) {
        toast.error("Session missing. Please login again.");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append('expertCode', expertCode);
      
      if (data.identityProof && data.identityProof.length > 0) {
        formData.append('file', data.identityProof[0]); 
      }

      await saveDoctorIdentity(formData);

      toast.success("Identity Proof uploaded successfully!");
      onNext(data);

    } catch (error: any) {
      console.error("API Error:", error);
      toast.error(error.response?.data?.message || "Failed to upload identity proof. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFileName(files[0].name);
      
      // 🔥 FIX: Set value and Force Validation immediately
      setValue("identityProof", files, { shouldValidate: true });
      trigger("identityProof"); 
      clearErrors("identityProof");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFileName(files[0].name);
      
      // 🔥 FIX: Set value and Force Validation immediately
      setValue("identityProof", files, { shouldValidate: true });
      trigger("identityProof");
      clearErrors("identityProof");
    }
  };

  const isStrictMode = !IS_DEV || TEST_REAL_API;

  return (
    <div className="bg-white rounded-lg shadow-xl p-8">
      
      <div className="flex justify-between items-start mb-6">
        <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Identity Proof</h2>
            <p className="text-gray-600">Please upload your identity proof</p>
        </div>
        
        {TEST_REAL_API && IS_DEV && (
          <div className="bg-yellow-500/20 text-yellow-700 py-1 px-3 rounded-full text-xs border border-yellow-500/50 font-medium">
            ⚠️ Real API Testing Enabled
          </div>
        )}
      </div>

      <div>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
            isDragging 
              ? 'border-cyan-500 bg-cyan-50' 
              : errors.identityProof 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-300 hover:border-cyan-400 bg-gray-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {/* 🔥 FIX: REMOVED ...register() FROM HERE */}
          <input
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            id="identity-upload"
          />
          <label htmlFor="identity-upload" className="cursor-pointer w-full h-full block">
            <div className="flex flex-col items-center justify-center">
              {fileName ? (
                <>
                  <FileText className="w-16 h-16 text-cyan-500 mb-3" />
                  <p className="text-sm font-medium text-gray-700">{fileName}</p>
                  <p className="text-xs text-gray-500 mt-1">Click to replace file</p>
                </>
              ) : (
                <>
                   <Upload className="w-16 h-16 text-gray-400 mb-3" />
                   <div className="bg-white border border-gray-300 text-cyan-500 px-6 py-2 rounded-lg mb-2 shadow-sm">
                      Upload File
                   </div>
                   <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                </>
              )}
            </div>
          </label>
        </div>
        
        {errors.identityProof && (
            <p className="mt-2 text-sm text-red-600">
                {errors.identityProof.message as string}
            </p>
        )}

        <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <p className="font-semibold text-gray-800 mb-2 text-sm">Acceptable Documents:</p>
          <ul className="grid grid-cols-2 gap-2 text-sm text-gray-600 list-disc list-inside">
            <li>Aadhar Card</li>
            <li>Driving License</li>
            <li>Voter Card</li>
            <li>Any other Govt. ID</li>
          </ul>
        </div>
      </div>

      <div className="text-center mt-8 space-y-3">
        
        {/* ⚡ DEV BUTTON */}
        {IS_DEV && !TEST_REAL_API && (
            <button
              type="button"
              onClick={handleDevSkip}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-2 px-12 rounded-lg transition-colors border-2 border-yellow-600 border-dashed"
            >
              ⚡ DEV MODE: Skip & Mock Verify
            </button>
        )}

        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || (isStrictMode && !isValid)}
          className={`w-full bg-cyan-400 hover:bg-cyan-500 text-white font-semibold py-3 px-12 rounded-lg transition-colors 
            ${(isSubmitting || (isStrictMode && !isValid)) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isSubmitting ? "Processing..." : (
             <span className="flex items-center justify-center gap-2">
               Proceed to Next Step <ChevronRight size={20} />
             </span>
          )}
        </button>
      </div>
    </div>
  );
}